/**
 * 🏁 Final-round modal state + handlers, as a reusable hook.
 *
 * Extracted VERBATIM from FinalRoundsSection (Aug 2026) so the Requests tab can embed a
 * candidate's FinalRoundCard — with working Propose/Book/Debrief actions — without a second copy
 * of the booking cascade. Two callers, one set of handlers: the Final Rounds tab itself, and the
 * per-request scheduling connection (RequestSchedulingConnection.tsx). Each caller mounts its own
 * modal instance; the state is per-caller on purpose, so opening a modal from a request card
 * cannot fight the Final Rounds tab over one shared slot.
 *
 * `fr-recheck` is deliberately NOT handled here — re-grading is role-level UI state (the spinner
 * on the accordion's 🔄 button), so it stays with FinalRoundsSection, which routes it before
 * delegating everything else to this hook.
 */
import { useState } from 'react';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import * as api from '../../services/taActionBoardApi';
import type {
  RoleBoard,
  SchedulingCandidate,
  StageRef,
  TrackingEntry,
  TrackingKind,
} from '../../types/taActionBoard';
import { debriefOfferAction, debriefRejectAction } from './debriefDecisionAction';
import { offerEmailAction } from './offerEmailAction';
import type { OfferedEmail } from './OfferedModalBody';
import type { PendingAction } from './usePendingActions';
import { FinalRoundModals, type BookPayload, type FinalRoundModalState } from './FinalRoundModals';
import type { FinalRoundAction } from './FinalRoundsTab';
import type { DebriefOutcome } from './DebriefDecisionBody';

export interface FinalRoundActionDeps {
  boardEmail: string;
  senderFirstName: string;
  /** Lever's stages in pipeline order — the post-debrief offer target. */
  stages: StageRef[];
  /** ACTIVE Lever archive reasons — the post-debrief rejection. Byte-exact. */
  archiveReasons: string[];
  /** The board's Google token — the proposal email sends as the recruiter. */
  gmailToken: string | null;
  /** Optimistic tracking update + POST (the view's applyTracking). */
  onApplyTracking: (
    opportunityId: string,
    kind: TrackingKind,
    slots: string[],
    note?: string | null
  ) => void;
  /** Local-only tracking update — for entries the SERVER already wrote (fr-booked). */
  onLocalTracking: (opportunityId: string, entry: TrackingEntry, status: string) => void;
  onMarkHandled: (opportunityId: string, label: string) => void;
  /** Queue the send behind the board's cancellable countdown. */
  onDefer: (action: Omit<PendingAction, 'secondsLeft'>) => void;
  /** Replace one role's finalRounds section (after template edits re-solve chains). */
  onFinalRoundsUpdate: (postingId: string, section: RoleBoard['finalRounds']) => void;
}

/** Fallback local entry when the server response carried no tracking entry. */
function bookedEntry(by: string): TrackingEntry {
  return { kind: 'fr-booked', slot: null, note: 'Final round booked', at: new Date().toISOString(), by };
}

export function useFinalRoundActions({
  boardEmail,
  senderFirstName,
  stages,
  archiveReasons,
  gmailToken,
  onApplyTracking,
  onLocalTracking,
  onMarkHandled,
  onDefer,
  onFinalRoundsUpdate,
}: FinalRoundActionDeps) {
  const [modal, setModal] = useState<FinalRoundModalState | null>(null);

  /**
   * The two post-debrief outcomes, both queued behind the board's countdown so
   * "Stop" means the Lever write never happened either.
   */
  const onDebriefOffer = (candidate: SchedulingCandidate, targetStageId: string) =>
    onDefer(debriefOfferAction({ candidate, boardEmail, targetStageId }));
  const onDebriefReject = (
    candidate: SchedulingCandidate,
    reason: string,
    email: { subject: string; bodyHtml: string; language: 'de' | 'en' } | null
  ) => onDefer(debriefRejectAction({ candidate, boardEmail, reason, email, gmailToken }));

  const handlers = {
    onSaveConfig: (role: RoleBoard, config: Parameters<typeof api.saveFinalRoundConfig>[1]) => {
      api
        .saveFinalRoundConfig(role.postingId, config)
        // boardEmail matters: the board owner is the default onsite contact and
        // must be free when the candidate arrives.
        .then(() => api.fetchFinalRoundChains(role.postingId, boardEmail))
        .then(({ config: saved, chains, error, diagnosis, lookaheadDays }) => {
          onFinalRoundsUpdate(role.postingId, {
            candidates: role.finalRounds.candidates,
            config: saved,
            chains,
            chainError: error,
            // Same fields the 🔄 re-check forwards (FinalRoundsSection.runRecheck) — dropping
            // them here erased the no-chain diagnosis whenever a template was saved.
            chainDiagnosis: diagnosis ?? null,
            ...(lookaheadDays ? { lookaheadDays } : {}),
          });
          showToast({ status: 'success', description: 'Round template saved — chains re-solved.' });
        })
        .catch((e) =>
          showToast({
            status: 'error',
            description: e instanceof Error ? e.message : 'Could not save the round template.',
          })
        );
    },

    /**
     * Same shape as the case-interview slot offer, INCLUDING the send: the
     * proposal goes out from the recruiter's own Gmail behind the board's
     * cancellable countdown, and the tracking note says which happened.
     */
    onPropose: (
      role: RoleBoard,
      candidate: SchedulingCandidate,
      slotLabels: string[],
      channel: 'phone' | 'email',
      email: OfferedEmail | null
    ) => {
      onApplyTracking(
        candidate.opportunityId,
        'fr-proposed',
        slotLabels,
        channel === 'email'
          ? email
            ? 'Final-round proposal emailed from the board'
            : 'Could not reach by phone — final-round proposal drafted as email'
          : 'Reached by phone'
      );
      // Channel-gated for the same reason as the case offer: told by phone
      // means told by phone, and this is the last gate before the inbox.
      if (channel === 'email' && email) {
        onDefer(
          offerEmailAction({
            candidate,
            role,
            email,
            gmailToken,
            kind: 'fr-invite',
            label: `Sending the final-round proposal to ${candidate.name}`,
          })
        );
      }
    },

    /**
     * The post-booking summary, sent as the recruiter behind the countdown. The
     * candidate's address is resolved from Lever by the proxy, never from here.
     */
    onSendSummary: (candidate: SchedulingCandidate, email: { subject: string; body: string }) => {
      onDefer({
        id: `fr-summary:${candidate.opportunityId}`,
        label: `Sending the final-round summary to ${candidate.name}`,
        run: () => {
          api
            .sendCandidateEmail(
              {
                opportunityId: candidate.opportunityId,
                subject: email.subject,
                body: email.body,
                kind: 'fr-invite',
                stageName: candidate.stageName,
              },
              gmailToken
            )
            .then((r) =>
              showToast({ status: 'success', description: `Final-round summary sent to ${r.to}.` })
            )
            .catch((e) =>
              showToast({
                status: 'error',
                description: `The summary was NOT sent: ${
                  e instanceof Error && e.message ? e.message : 'unknown error'
                }`,
              })
            );
        },
        undo: () =>
          showToast({ status: 'info', description: `Stopped — no summary was sent to ${candidate.name}.` }),
      });
    },

    onBook: (
      role: RoleBoard,
      candidate: SchedulingCandidate,
      payload: BookPayload,
      chainLabel: string
    ) => {
      // The confirmation itself is tracked first (proposed → CONFIRMED → booked)…
      onApplyTracking(candidate.opportunityId, 'fr-confirmed', [chainLabel]);
      // …then the cascade runs server-side; fr-booked is written by the server
      // (it carries the created calendar event ids), so it is mirrored locally
      // WITHOUT another POST.
      api
        .bookFinalRound({
          postingId: role.postingId,
          opportunityId: candidate.opportunityId,
          boardEmail,
          ...payload,
        })
        .then((r) => {
          onLocalTracking(candidate.opportunityId, r.entry ?? bookedEntry(boardEmail), 'final round booked');
          /**
           * A seat that did not reach Lever is named, and downgrades the toast to
           * a warning — that interviewer has a calendar invite but no feedback
           * form, and only the recruiter can put it right. Never an error: the
           * round IS booked, and the candidate is about to be told so.
           */
          const missed = (r.leverSeats || []).filter((s) => !s.created);
          const leverNote = missed.length
            ? ` Not in Lever: ${missed.map((s) => s.label).join(', ')} — add the interview there so the feedback form exists.`
            : '';
          // A room that was asked for but not reserved needs a manual booking —
          // say so and downgrade the toast, the same as a missed Lever seat.
          const roomFailed = r.room ? !r.room.booked : false;
          const roomNote = r.room
            ? r.room.booked
              ? ` Room ${r.room.name} booked.`
              : ' No free room found — book one by hand.'
            : '';
          /**
           * The agenda's outcome, for BOTH destinations.
           *
           * This used to read `slack.sent ? ` posted to ${slack.channel}.` : …`,
           * which was wrong twice on the DM path — the default path. A
           * successful DM run has no `channel`, so it printed "posted to
           * undefined"; and a run where every DM failed had no `error` either,
           * so it printed NOTHING and a booking whose agenda reached nobody
           * read as a clean success (Theresa, Sep 2026).
           */
          const slackNote = r.slack.channel
            ? r.slack.sent
              ? ` Agenda posted to ${r.slack.channel}.`
              : ` ${r.slack.error ?? `Agenda NOT posted to ${r.slack.channel}.`}`
            : r.slack.dms?.length
              ? ` Agenda DM'd to ${r.slack.dms.length} ${r.slack.dms.length === 1 ? 'person' : 'people'}.` +
                (r.slack.error ? ` ${r.slack.error}` : '')
              : ` ⚠ The agenda was NOT sent. ${r.slack.error ?? 'Slack did not accept it.'}`;
          const slackFailed = !r.slack.sent || (r.slack.failed?.length ?? 0) > 0;
          showToast({
            status: r.ok && missed.length === 0 && !roomFailed && !slackFailed ? 'success' : 'warning',
            description:
              (r.ok
                ? `Final round booked — ${r.created} calendar events, holds released.` + slackNote
                : `Partially booked — ${r.failed.length} calendar writes failed. Check the calendars.`) +
              leverNote +
              roomNote,
          });
          setModal({
            kind: 'fr-summary',
            candidate,
            summary: r.summary,
            summaries: r.summaries,
            summaryLanguage: r.summaryLanguage,
            candidateEmail: r.candidateEmail,
          });
        })
        .catch((e) =>
          showToast({
            status: 'error',
            description: e instanceof Error ? e.message : 'Could not book the final round.',
          })
        );
    },

    /**
     * Back to the top of the final-round list, options free to propose again.
     * Records only: nothing was booked, so there is no calendar entry to undo
     * and no candidate notice for the board to invent.
     */
    onWithdraw: (candidate: SchedulingCandidate) => {
      onApplyTracking(
        candidate.opportunityId,
        'fr-withdrawn',
        [],
        'Proposed final-round times withdrawn'
      );
      showToast({
        status: 'success',
        description: `Withdrawn — ${candidate.name} is back on the final-round list and those options are free to propose again.`,
      });
    },

    /**
     * The debrief's outcome. The tracking entry is written FIRST in both
     * branches, so the debrief is recorded even if the Lever write fails — that
     * conversation happened whatever happens next.
     */
    onDebriefDecision: (
      _role: RoleBoard,
      candidate: SchedulingCandidate,
      outcome: DebriefOutcome
    ) => {
      onApplyTracking(
        candidate.opportunityId,
        'fr-debrief-done',
        [],
        outcome.kind === 'offer' ? 'Debrief done — moving to offer' : 'Debrief done — rejecting'
      );
      if (outcome.kind === 'offer') {
        onMarkHandled(candidate.opportunityId, 'Moving to offer');
        onDebriefOffer(candidate, outcome.targetStageId);
        return;
      }
      /**
       * "Rejected", never "email sent". The send has not happened yet — it sits
       * behind the board's countdown and can be stopped or fail — and a card
       * claiming an email the candidate never got is the one mistake a
       * recruiter cannot see. The send reports itself in its own toast.
       */
      onMarkHandled(candidate.opportunityId, 'Rejected');
      onDebriefReject(candidate, outcome.reason, outcome.email);
    },
  };

  /** Route one card/tab action into this hook's modal slot. `fr-recheck` is the caller's. */
  const onAction = (role: RoleBoard, action: Exclude<FinalRoundAction, { type: 'fr-recheck' }>) => {
    if (action.type === 'fr-config')
      return setModal({ kind: 'fr-config', role, suggestion: action.suggestion });
    if (action.type === 'fr-ask') {
      // The ask names the candidate, so pass the one waiting — with more
      // than one, the first (the list is longest-wait-first).
      return setModal({
        kind: 'fr-ask',
        role,
        candidate: role.finalRounds.candidates[0] ?? null,
        options: action.options,
      });
    }
    if (action.type === 'fr-propose')
      return setModal({
        kind: 'fr-propose',
        role,
        candidate: action.candidate,
        preselected: action.preselected,
      });
    /**
     * The debrief modal needs the pipeline's stages (to move to offer) and the
     * ACTIVE Lever archive reasons (to reject). Both are board-level facts, so
     * they are threaded in rather than fetched a second time here.
     */
    if (action.type === 'fr-debrief')
      return setModal({
        kind: 'fr-debrief',
        role,
        candidate: action.candidate,
        stages,
        archiveReasons,
      });
    return setModal({ kind: action.type, role, candidate: action.candidate });
  };

  const modals = (
    <FinalRoundModals
      modal={modal}
      senderFirstName={senderFirstName}
      gmailConnected={!!gmailToken}
      boardEmail={boardEmail}
      onClose={() => setModal(null)}
      handlers={handlers}
    />
  );

  return { onAction, modals };
}
